import React, { lazy, useState, useEffect } from "react";
// P1.11 — QR shortcut hidden in favor of the dedicated /search page. Keep the
// import + JSX commented (below) so we can revert if product changes course.
// import NavQR from "../../assets/Create-Account/navQR.svg";
import { Link, useLocation } from "react-router-dom";
import search from "../../assets/generalSearch.svg";
import brandNavbarLogo from "../../assets/brand/navbar-logo.svg";
import Button from "./Button";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation } from "@tanstack/react-query";
import { getLocalToken } from "../../Helper/getToken";
import { encrptDecrptobj } from "../../utils/decryptandencrypt";
import { isPaid } from "../../utils/paymentNav";

// import { useNavigate } from "react-router-dom";

const loadAuthActions = () => import("../../redux/actions/auth");
const getTopNodesLazy = async () => (await loadAuthActions()).getTopNodes();
const getProfileDataLazy = async () => (await loadAuthActions()).getProfileData();
const searchNodeLazy = async (value) => (await loadAuthActions()).searchNode(value);
const NavDrawer = lazy(() => import("./Drawer/NavDrawer"));
const NotificationsDrawer = lazy(() => import("./Drawer/NotificationsDrawer"));

const NavBar = ({ searchOpen }) => {
  const [opensearch, setOpensearch] = useState(false);

  const navigate = useNavigate();
  const token = getLocalToken();
  const [state, setState] = useState({
    tenNodes: [],
    inputData: "",
  });

  // P2.9 — avatar populates instantly from the persisted Redux userData (set by
  // Login/Signin onSuccess) instead of waiting on the secondary getProfileData
  // round-trip, which on a cold Lambda + cold Mongo can take 5–10s and leaves
  // the navbar avatar blank in the meantime.
  const userData = useSelector((state) => state.auth.userData);
  const [UserProfileData, setUserProfileData] = useState(userData || "");

  const navSearch = () => {
    setOpensearch((opensearch) => !opensearch);
  };

  useEffect(() => {
    const handleScroll = () => {
      if (opensearch) {
        setOpensearch(false);
      }
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [opensearch]);

  const { data: top10Nodes } = useQuery({
    queryKey: ["TopNodes"],
    queryFn: getTopNodesLazy,
    enabled: !!token,
  });
  const searchNodes = useMutation({
    mutationFn: searchNodeLazy,
    // isPending: userSearchLaoder,
  });

  // P1.11 — the navbar input is now a launchpad, not a live search surface.
  // ObjectIds short-circuit to the existing join flow; any other 2+ char
  // query hands off to the dedicated /search page on Enter.
  const OBJECT_ID_RE = /^[a-f0-9]{24}$/i;
  const submitSearch = (raw) => {
    const v = String(raw || "").trim();
    if (!v) return;
    if (OBJECT_ID_RE.test(v)) {
      searchNodes.mutate(v, {
        onSuccess: (data) => {
          setState((prev) => ({ ...prev, inputData: "" }));
          navigate(`/${v}`, { state: data });
          setOpensearch(false);
        },
      });
      return;
    }
    if (v.length >= 2) {
      setState((prev) => ({ ...prev, inputData: "" }));
      setOpensearch(false);
      navigate(`/search?q=${encodeURIComponent(v)}`);
    }
  };

  const logout = (res) => {};

  useEffect(() => {
    setState((prev) => ({
      ...prev,
      tenNodes: top10Nodes,
    }));
  }, [top10Nodes]);

  const {
    data,
    refetch: getAgainUserProfile,
    isPending,
  } = useQuery({
    queryKey: ["user"],
    queryFn: getProfileDataLazy,
    enabled: !!token,
  });

  useEffect(() => {
    if (userData) setUserProfileData(userData);
  }, [userData]);

  useEffect(() => {
    if (data) {
      const userProfileEncryptes = encrptDecrptobj(data, "decrypt");
      setUserProfileData(userProfileEncryptes);
    }
  }, [data]);

  const [isLoggedIn, setIsLoggedIn] = useState(getLocalToken());
  return (
    <nav className=" top-menu w-full  flex flex-col relative z-20">
      <section className="glass-nav-row xl:py-3 lg:py-2 md:py-2 xs:py-1 py-2 2 flex items-center justify-start 4xll:paddingX44 4xl:paddingX40 3xll:paddingX36 3xl:paddingX36 2xll:paddingX32 2xl:paddingX28 xl:paddingX lg:paddingX20 sm:paddingX12 px-3 xs:px-2 md:px-6">
        <Link to="/" className="flex-center shrink-0" aria-label="Eqlize Now home">
          <img
            src={brandNavbarLogo}
            alt="Eqlize Now"
            width="260"
            height="96"
            decoding="async"
            className="glass-nav-logo 4xll:h-[16rem] 4xl:h-[14rem] 3xll:h-[11.5rem] 3xl:h-[10.5rem] 2xll:h-[9.5rem] 2xl:h-[8.5rem] xl:h-[7rem] lg:h-[7.5rem] md:h-[5rem] sm:h-[4rem] xs:h-[3rem] h-7 w-auto"
          />
        </Link>

        <div className="ml-auto flex-center xl:gap-x-9 lg:gap-x-7 md:gap-x-6 gap-x-2 sm:gap-x-4">
          <div className="glass-nav-search w-[216px] flex items-center 4xll:w-[800px] 4xl:w-[700px] 3xll:w-[600px]  3xl:w-[500px] 2xll:w-[400px] 2xl:w-[300px]  sm:hidden xs:hidden relative px-4 py-2 gap-2">
            <>
              {" "}
              <img src={search} alt="" className="w-4 h-4 opacity-70 shrink-0" />
              <input
                className="bg-transparent border-none focus:outline-none w-full text-ink placeholder:text-ink-muted 4xll:text-40 4xl:text-38 3xll:text-34  3xl:text-30 2xll:text-25 2xl:text-22"
                placeholder={"Find Campaigns"}
                type={"text"}
                value={state.inputData}
                onChange={(e) =>
                  setState((prev) => ({ ...prev, inputData: e.target.value }))
                }
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    submitSearch(state.inputData);
                  }
                }}
              />
            </>
          </div>

          <div
            onClick={navSearch}
            className="glass-nav-icon-btn 4xll:hidden 4xl:hidden 3xll:hidden 3xl:hidden 2xll:hidden 2xl:hidden xl:hidden lg:hidden md:hidden flex h-10 w-10 items-center justify-center rounded-full cursor-pointer"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth="1.5"
              stroke="currentColor"
              className="h-5 w-5"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z"
              />
            </svg>
          </div>
          {/* P1.11 — QR shortcut hidden; campaigns are now found via /search.
              Preserve markup commented out so we can re-enable in seconds. */}
          {/* <div onClick={() => navigate("/node-access")}>
            <img
              src={NavQR}
              alt="Open node access"
              className="cursor-pointer 4xll:size-16 4xl:size-14 3xll:size-12  3xl:size-11 2xll:size-10 2xl:size-10 xl:w-[30px] lg:w-[30px] w-[30px] sm:w-[24px] xs:w-[24px]"
            />
          </div> */}
          {token !== null && isPaid(UserProfileData?.paidStatus) ? (
            <NotificationsDrawer />
          ) : (
            ""
          )}

          {token !== null ? (
            <>
              <NavDrawer UserProfileData={UserProfileData} />
              <div className="ml-[-16px]  xs:hidden">
                <h1 className=" hidden 4xll:text-[42px] 4xl:text-[38px] 3xll:text-[35px] 3xl:text-[32px] 2xll:text-[28px] 2xl:text-[26px] xl:text-[23px] lg:text-[20px] md:text-[17px] sm:text-[15px] xs:text-[14px] font-bold">
                  {UserProfileData?.userName}
                </h1>
              </div>
            </>
          ) : (
            <div className="flex items-center">
              <button
                type="button"
                onClick={() => navigate("/sign-in")}
                className="btn-glass-primary inline-flex h-10 items-center justify-center px-4 sm:px-5 text-[13px] sm:text-[14px]"
              >
                Sign In
              </button>
            </div>
          )}
        </div>
      </section>

      {opensearch ? (
        <section className=" flex flex-col justify-center items-center w-[100%]  md:hidden lg:hidden xl:hidden 4xll:hidden 4xl:hidden 3xll:hidden   3xl:hidden 2xll:hidden 2xl:hidden sm:px-14 xs:px-4 mb-[20px]  ">
          <div className="glass-nav-search flex items-center w-full px-4 py-2 gap-2">
            <img src={search} alt="" className="w-4 h-4 opacity-70 shrink-0" />
            <input
              className="bg-transparent border-none focus:outline-none w-full text-ink placeholder:text-ink-muted"
              placeholder={"Find Campaigns"}
              type={"text"}
              value={state.inputData}
              onChange={(e) =>
                setState((prev) => ({ ...prev, inputData: e.target.value }))
              }
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  submitSearch(state.inputData);
                }
              }}
            />
          </div>
        </section>
      ) : null}
    </nav>
  );
};

export default NavBar;
